import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useSetDefaultProject,
} from '../hooks/useProjects';
import { useContacts } from '../hooks/useContacts';
import useProjectStore from '../stores/projectStore';
import Modal from '../components/ui/Modal';
import StatCard from '../components/ui/StatCard';

const COLORS = ['#0C9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#F97316', '#1C2E3C'];

function ProjectIcon({ name, color }) {
  const initials = (name || '?')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center text-[14px] font-bold text-white shrink-0"
      style={{ backgroundColor: color || '#0C9488' }}
    >
      {initials}
    </div>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useProjects();
  const { data: contactsData } = useContacts();
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const deleteMutation = useDeleteProject();
  const setDefaultMutation = useSetDefaultProject();
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const projects = data?.projects || [];
  const contacts = contactsData?.data || contactsData || [];

  const stats = {
    total: projects.length,
    cro: projects.reduce((s, p) => s + (p.analyses_count || 0), 0),
    seo_kw: projects.reduce((s, p) => s + (p.seo_keywords_count || 0), 0),
    seo_m: projects.reduce((s, p) => s + (p.seo_measures_count || 0), 0),
  };

  const openNew = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setShowModal(true);
  };

  // Auto-Öffnen via ?new=1 (z.B. aus dem Sidebar-CTA "Projekt anlegen").
  // Parameter danach entfernen, damit Reload/Zurück das Modal nicht erneut öffnet.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditing(null);
      setShowModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      name: fd.get('name'),
      domain: fd.get('domain') || null,
      gsc_site_url: fd.get('gsc_site_url') || null,
      contact_id: fd.get('contact_id') ? Number(fd.get('contact_id')) : null,
      color: fd.get('color') || null,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, ...payload }, { onSuccess: () => setShowModal(false) });
    } else {
      createMutation.mutate(payload, { onSuccess: () => setShowModal(false) });
    }
  };

  const handleDelete = (p) => {
    if (p.is_default) return;
    if (!confirm(`Projekt "${p.name}" wirklich löschen? Daten bleiben erhalten, der Bezug zum Projekt geht aber verloren.`)) return;
    deleteMutation.mutate(p.id);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[23px] font-bold text-gray-900">Projekte</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Jedes Projekt bündelt CRO-Analysen, SEO-Rankings und Maßnahmen für eine Domain.
          </p>
        </div>
        <button
          onClick={openNew}
          className="px-3.5 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
        >
          + Neues Projekt
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3.5 mb-5">
        <StatCard icon="⌘" value={stats.total} label="Projekte" sub="Aktiv" />
        <StatCard icon="⟳" value={stats.cro} label="CRO-Analysen" sub="Über alle Projekte" />
        <StatCard icon="◉" value={stats.seo_kw} label="SEO-Keywords" sub="Getrackt" />
        <StatCard icon="☑" value={stats.seo_m} label="SEO-Maßnahmen" sub="Gesamt" />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-400 text-[13px] bg-white rounded-xl">Lade…</div>
      ) : projects.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-xl border border-black/[.07]">
          <div className="text-[40px] mb-3">⌘</div>
          <div className="text-[15px] font-semibold text-gray-700 mb-1">Noch kein Projekt</div>
          <div className="text-[13px] text-gray-500 mb-4">Lege dein erstes Projekt an, um CRO + SEO sauber pro Kunde zu organisieren.</div>
          <button onClick={openNew} className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium">
            + Erstes Projekt anlegen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3.5">
          {projects.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4 flex flex-col gap-3 group hover:border-teal/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <ProjectIcon name={p.name} color={p.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-[14px] text-gray-900 truncate">{p.name}</div>
                    {p.is_default && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-teal/10 text-teal">DEFAULT</span>
                    )}
                  </div>
                  {p.domain && (
                    <a
                      href={p.domain.startsWith('http') ? p.domain : `https://${p.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11.5px] text-gray-500 hover:text-teal truncate block font-mono"
                    >
                      {p.domain}
                    </a>
                  )}
                  {p.contact?.name && (
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      Kunde: {p.contact.name}{p.contact.company ? ` · ${p.contact.company}` : ''}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded-md py-1.5">
                  <div className="text-[14px] font-bold text-gray-900">{p.analyses_count || 0}</div>
                  <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">CRO</div>
                </div>
                <div className="bg-gray-50 rounded-md py-1.5">
                  <div className="text-[14px] font-bold text-gray-900">{p.seo_keywords_count || 0}</div>
                  <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">Keywords</div>
                </div>
                <div className="bg-gray-50 rounded-md py-1.5">
                  <div className="text-[14px] font-bold text-gray-900">{p.seo_measures_count || 0}</div>
                  <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">Maßnahmen</div>
                </div>
              </div>

              <div className="flex items-center gap-1.5 pt-1 border-t border-gray-50">
                <button
                  onClick={() => {
                    setCurrentProject(p.id);
                    navigate('/');
                  }}
                  className="flex-1 text-[12px] text-teal font-medium hover:bg-teal/[.06] rounded-md py-1.5"
                >
                  Öffnen →
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="text-[11px] text-gray-500 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-50"
                >
                  Bearbeiten
                </button>
                {!p.is_default && (
                  <button
                    onClick={() => setDefaultMutation.mutate(p.id)}
                    className="text-[11px] text-gray-400 hover:text-teal px-2 py-1.5 rounded-md hover:bg-gray-50"
                    title="Als Standard setzen"
                  >
                    ★
                  </button>
                )}
                {!p.is_default && (
                  <button
                    onClick={() => handleDelete(p)}
                    className="text-[11px] text-gray-300 hover:text-red-500 px-2 py-1.5 rounded-md hover:bg-gray-50"
                    title="Löschen"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <h2 className="text-[16px] font-bold mb-4">
            {editing ? 'Projekt bearbeiten' : 'Neues Projekt'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Name *</label>
                <input
                  name="name"
                  required
                  defaultValue={editing?.name || ''}
                  placeholder="z.B. Acme Corp"
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Domain</label>
                <input
                  name="domain"
                  defaultValue={editing?.domain || ''}
                  placeholder="acme.com"
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal font-mono"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">
                  GSC Site URL
                  <span className="text-gray-400 font-normal ml-1">— wird für Search Console Fetches genutzt</span>
                </label>
                <input
                  name="gsc_site_url"
                  defaultValue={editing?.gsc_site_url || ''}
                  placeholder="https://acme.com/"
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal font-mono"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Kunde / Kontakt</label>
                <select
                  name="contact_id"
                  defaultValue={editing?.contact_id || ''}
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal bg-white"
                >
                  <option value="">— Keiner —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.company ? ` (${c.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Farbe</label>
                <div className="flex gap-1.5">
                  {COLORS.map((c) => (
                    <label key={c} className="cursor-pointer">
                      <input
                        type="radio"
                        name="color"
                        value={c}
                        defaultChecked={(editing?.color || '#0C9488') === c}
                        className="peer sr-only"
                      />
                      <div
                        className="w-7 h-7 rounded-md border-2 border-transparent peer-checked:border-gray-900 peer-checked:scale-110 transition-transform"
                        style={{ backgroundColor: c }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending ? 'Speichere…' : (editing ? 'Speichern' : 'Anlegen')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
